/** Listing media kind — parity with Dispatch media_intake / OS media_kind.
 *
 * Canada uploads has no media_type column. Desk tabs infer drone/reel/
 * twilight/staging/floorplan from durable filename cues and DJI_ prefixes.
 * A hollow deliverable type of "photo" (legacy default) must not mask those cues.
 */

const GENERIC = new Set(['', 'photo', 'hdr', 'still', 'stills', 'image', 'photos', 'photography']);

export function inferMediaType(filename, mimeType, explicit) {
  const raw = String(explicit || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
  const aliases = {
    photos: 'hdr',
    still: 'hdr',
    stills: 'hdr',
    photography: 'hdr',
    aerial: 'drone',
    dji: 'drone',
    video: 'reel',
    cinematic: 'reel',
    cinematic_reel: 'reel',
    dusk: 'twilight',
    day_to_dusk: 'twilight',
    virtual_staging: 'staging',
    stage: 'staging',
    photo: 'hdr',
    plan: 'plan',
    floorplan: 'plan',
    floor_plan: 'plan',
    roomplan: 'plan',
    room_plan: 'plan',
    cubicasa: 'plan',
  };
  const known = new Set(['hdr', 'photo', 'drone', 'reel', 'twilight', 'staging', 'plan', 'floorplan', 'visit', 'tour', 'splat']);
  if (raw && !GENERIC.has(raw)) {
    if (known.has(raw)) {
      if (raw === 'photo') return 'hdr';
      if (raw === 'floorplan') return 'plan';
      return raw;
    }
    if (aliases[raw]) return aliases[raw];
  }

  const name = String(filename || '').toLowerCase();
  const mime = String(mimeType || '').toLowerCase();
  if (
    name.includes('floorplan') ||
    name.includes('floor_plan') ||
    name.includes('floor-plan') ||
    name.includes('cubicasa') ||
    name.includes('roomplan') ||
    name.includes('room_plan') ||
    name.includes('room-plan') ||
    /\.(pdf)$/i.test(name) ||
    mime === 'application/pdf' ||
    mime.endsWith('/pdf')
  ) {
    return 'plan';
  }
  // Exact basename plan/plans (CubiCasa) — backend media_kind parity.
  // Do NOT use name.includes('floor'): ground_floor_living.jpg is a listing still.
  const stem = name.includes('.') ? name.slice(0, name.lastIndexOf('.')) : name;
  if (stem === 'plan' || stem === 'plans') {
    return 'plan';
  }
  // Delimited "plan" token: "Floor Plan.png" / "level-1-plan.jpg" — not airplane/plant.
  if (/(?:^|[_\s-])plan(?:[_\s.-]|$)/.test(name)) {
    return 'plan';
  }
  if (name.includes('stag')) return 'staging';
  if (name.includes('twilight') || name.includes('dusk') || name.includes('golden')) {
    return 'twilight';
  }
  if (name.includes('drone') || name.includes('aerial') || name.startsWith('dji')) {
    return 'drone';
  }
  if (name.includes('reel') || name.includes('cinematic')) return 'reel';
  if (mime.startsWith('video/') || /\.(mp4|mov|m4v|webm|avi)$/i.test(name)) {
    return 'reel';
  }
  if (raw && known.has(raw)) {
    if (raw === 'photo') return 'hdr';
    if (raw === 'floorplan') return 'plan';
    return raw;
  }
  if (raw && aliases[raw]) return aliases[raw];
  return 'hdr';
}

/** Desk media library tab label for a deliverable row. */
export function deliverableTab(file) {
  const f = file || {};
  const inferred = inferMediaType(f.filename, f.mime_type, f.type || f.media_type || f.kind);
  if (inferred === 'drone') return 'Drone';
  if (inferred === 'reel') return 'Reel';
  if (inferred === 'plan' || inferred === 'floorplan') return 'Plan';
  // twilight / staging / hdr → Photos (listing stills)
  return 'Photos';
}

/**
 * Split public share/listing photos[] by inferred kind.
 * Prefer API type when specific; hollow photo/hdr must not mask DJI_/aerial_/reel_
 * filename cues (deliverableTab parity). Listing must not dump drone/reel into Photos.
 */
export function partitionSharePhotos(photos) {
  const list = Array.isArray(photos) ? photos : [];
  const kindOf = (ph) =>
    inferMediaType(ph?.filename, ph?.mime_type, ph?.type || ph?.media_type || ph?.kind);
  const stillPhotos = list.filter((ph) => {
    const t = kindOf(ph);
    return !['drone', 'reel', 'video', 'plan', 'floorplan', 'visit', 'tour', 'splat'].includes(t);
  });
  const dronePhotos = list.filter((ph) => kindOf(ph) === 'drone');
  const reelPhotos = list.filter((ph) => ['reel', 'video'].includes(kindOf(ph)));
  return { stillPhotos, dronePhotos, reelPhotos };
}
